/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import UndoIcon from '@atlaskit/icon/core/undo';
import Popup from '@atlaskit/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { Sources as SourcesType } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type ContextInfo } from '../context-banner/useActiveContext';

import { messages } from './messages';
import { RotatedSourceLogoStack, type SpaceSourceResponse } from './RotatedSourceLogoStack';

const ANALYTICS_CHANNEL = 'aiMate';
const ACTIVE_CONTEXT_SOURCE_TYPE = 'active_context';

const LazySourcesPopupContent = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/sources-popup-content" */
		'@atlassian/conversation-assistant-content-renderer/ui/message-renderer/SourcesPopupContent'
	).then((module) => ({ default: module.SourcesPopupContent })),
);

const styles = cssMap({
	bannerWrapper: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.150'),
		minHeight: '40px',
	},
	clickableBannerWrapper: {
		width: '100%',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	contentContainer: {
		maxWidth: '100%',
		overflow: 'hidden',
	},
	contextLabel: {
		flexShrink: 0,
	},
	// Mirror the ContextBanner: the "referencing" icon is a flipped UndoIcon
	// (acts visually as a back-reference arrow) until a dedicated icon exists.
	referencingDocumentIcon: {
		transform: 'rotate(180deg)',
	},
	zeroSourcesBanner: {
		display: 'flex',
		gap: token('space.100'),
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.200'),
	},
	zeroSourcesIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '48px',
		height: '48px',
		flexShrink: 0,
		marginBlockStart: token('space.050'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
	},
	zeroSourcesIconGlyph: {
		display: 'flex',
		transform: 'scale(1.85)',
	},
	zeroSourcesLayout: {
		minWidth: '0px',
	},
	zeroSourcesContent: {
		minWidth: '0px',
		flexGrow: 1,
		paddingBlockStart: token('space.050'),
	},
	fullWidthAction: {
		width: '100%',
	},
});

type PopupSourceAnalyticsMetadata = {
	isThirdParty: boolean;
};

const isThirdPartySpaceSource = (source: SpaceSourceResponse): boolean =>
	!source.ari || source.ari === source.url;

const mapSourcesForPopup = (
	sources: SpaceSourceResponse[],
	activeContext?: ContextInfo | null,
): {
	popupSources: SourcesType;
	sourceAnalyticsMetadata: PopupSourceAnalyticsMetadata[];
} => {
	const spaceSources = sources
		.filter((source) => source.url)
		.map((source) => ({
			source: {
				ari: source.ari === source.url ? '' : source.ari,
				title: source.name ?? source.url,
				type: source.sourceType,
				url: source.url,
				lastModified: '',
				product: source.sourceType,
			},
			metadata: {
				isThirdParty: isThirdPartySpaceSource(source),
			},
		}));

	const activeContextSource =
		activeContext?.url && activeContext.title
			? [
					{
						source: {
							ari: '',
							title: activeContext.title,
							type: ACTIVE_CONTEXT_SOURCE_TYPE,
							url: activeContext.url,
							lastModified: '',
							product: ACTIVE_CONTEXT_SOURCE_TYPE,
						},
						metadata: {
							isThirdParty: false,
						},
					},
				]
			: [];

	const mappedSources = [...spaceSources, ...activeContextSource];

	return {
		popupSources: mappedSources.map(({ source }) => source),
		sourceAnalyticsMetadata: mappedSources.map(({ metadata }) => metadata),
	};
};

type SpaceContextBannerProps = {
	sourceCount?: number;
	sources?: SpaceSourceResponse[];
	activeContext?: ContextInfo | null;
	hasActiveContext?: boolean;
	zeroSourcesAction?: React.ReactNode;
};

export const SpaceContextBanner = ({
	sourceCount,
	sources = [],
	activeContext,
	hasActiveContext = false,
	zeroSourcesAction,
}: SpaceContextBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isZeroSourcesBannerDismissed, setIsZeroSourcesBannerDismissed] = useState(false);
	const isOpenRef = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { popupSources, sourceAnalyticsMetadata } = useMemo(
		() => mapSourcesForPopup(sources, activeContext),
		[sources, activeContext],
	);
	const hasContextSource = activeContext ? true : hasActiveContext;

	const fireTriggerAnalytics = useCallback(
		(state: 'open' | 'closed') => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sources',
				attributes: {
					source: 'spaceContextBanner',
					state,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const handleSourceLinkClick = useCallback(
		(type: string, ari?: string, product?: string, index?: number) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'sourceLink',
				attributes: {
					index,
					is3pLink: sourceAnalyticsMetadata[index ?? -1]?.isThirdParty ?? !ari,
					product,
					source: 'spaceContextBanner',
					sourceType: type,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, sourceAnalyticsMetadata],
	);

	const setPopupOpen = useCallback(
		(nextIsOpen: boolean) => {
			if (isOpenRef.current === nextIsOpen) {
				return;
			}

			fireTriggerAnalytics(nextIsOpen ? 'open' : 'closed');
			isOpenRef.current = nextIsOpen;
			setIsOpen(nextIsOpen);
		},
		[fireTriggerAnalytics],
	);

	const handleClose = useCallback(() => {
		setPopupOpen(false);
	}, [setPopupOpen]);

	const handleToggle = useCallback(() => {
		setPopupOpen(!isOpenRef.current);
	}, [setPopupOpen]);

	const renderPopupContent = useCallback(
		() => (
			<React.Suspense fallback={null}>
				<LazySourcesPopupContent
					sources={popupSources}
					onClickSourceHandler={handleSourceLinkClick}
					onClose={handleClose}
				/>
			</React.Suspense>
		),
		[handleClose, handleSourceLinkClick, popupSources],
	);

	if (!popupSources.length && sourceCount === undefined) {
		return null;
	}

	const effectiveSourceCount =
		popupSources.length > 0 ? popupSources.length : (sourceCount ?? 0) + (hasContextSource ? 1 : 0);

	if (effectiveSourceCount === 0 && zeroSourcesAction && isZeroSourcesBannerDismissed) {
		return null;
	}

	if (effectiveSourceCount === 0 && zeroSourcesAction) {
		return (
			<Stack xcss={styles.zeroSourcesBanner} space="space.100">
				<Inline xcss={styles.zeroSourcesLayout} space="space.150" alignBlock="start">
					<Box xcss={styles.zeroSourcesIcon} aria-hidden="true">
						<Box xcss={styles.zeroSourcesIconGlyph}>
							<PageIcon label="" />
						</Box>
					</Box>
					<Box xcss={styles.zeroSourcesContent}>
						<Stack space="space.050">
							<Text weight="bold">{formatMessage(messages.zeroSourcesTitle)}</Text>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.zeroSourcesDescription)}
							</Text>
						</Stack>
					</Box>
				</Inline>
				<Stack space="space.075">
					{zeroSourcesAction && <Box xcss={styles.fullWidthAction}>{zeroSourcesAction}</Box>}
					<Button
						appearance="default"
						onClick={() => setIsZeroSourcesBannerDismissed(true)}
						shouldFitContainer
					>
						{formatMessage(messages.zeroSourcesDismissLabel)}
					</Button>
				</Stack>
			</Stack>
		);
	}

	const bannerContent = (
		<Inline xcss={styles.contentContainer} space="space.050" alignBlock="center">
			<Inline xcss={styles.contextLabel} space="space.050" alignBlock="center">
				<Inline xcss={styles.referencingDocumentIcon} space="space.050">
					<UndoIcon size="small" label="" color={token('color.text.subtlest')} />
				</Inline>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.referencingSourcesLabel, {
						count: effectiveSourceCount,
					})}
				</Text>
				<RotatedSourceLogoStack sources={sources} />
			</Inline>
		</Inline>
	);

	if (popupSources.length === 0) {
		return <Box xcss={styles.bannerWrapper}>{bannerContent}</Box>;
	}

	return (
		<Popup
			placement="top-start"
			isOpen={isOpen}
			onClose={handleClose}
			content={renderPopupContent}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					xcss={cx(styles.bannerWrapper, styles.clickableBannerWrapper)}
					aria-expanded={isOpen}
					aria-haspopup="dialog"
					aria-label={formatMessage(messages.viewSourcesLabel, {
						count: effectiveSourceCount,
					})}
					onClick={handleToggle}
				>
					{bannerContent}
				</Pressable>
			)}
		/>
	);
};
